import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, Plus, Loader2, Trash2, Sparkles, FileText, X,
  ArrowRight, Image as ImgIcon, Upload, Calendar, Fingerprint,
  Copy, Check, ChevronDown,
  Share2, Facebook, MessageCircle, Twitter, EyeOff, Globe, Eye,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/components/EmptyState";
import RichText from "@/components/RichText";

const token = () => (typeof window !== "undefined" ? localStorage.getItem("tt_token") : null);
const inlineUrl = (docId) => `${API}/documents/${docId}/download?token=${encodeURIComponent(token() || "")}&inline=1`;
const compositeUrl = (compId) => `${API}/comparisons/${compId}/composite?token=${encodeURIComponent(token() || "")}`;

/**
 * PhotoComparisonTab
 *
 * Tenants build side-by-side comparisons of THEIR dated/hashed photo vs an
 * AGENT'S claim photo (e.g. agent says "fans are clean" but tenant has a
 * Photo Trail entry from last week showing visible dust). Compass writes a
 * plain-English analysis of the differences, and the user can drop the
 * comparison into a letter or email back to the agent.
 */
export default function PhotoComparisonTab({ caseId, caseState }) {
  const { user } = useAuth();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null); // currently-opened comparison for detail view

  const reload = async () => {
    const r = await api.get(`/cases/${caseId}/comparisons`);
    setComparisons(r.data);
  };

  useEffect(() => {
    let alive = true;
    api.get(`/cases/${caseId}/comparisons`)
      .then(r => alive && setComparisons(r.data))
      .catch(e => alive && toast.error(e.response?.data?.detail || "Couldn't load comparisons"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [caseId]);

  const onDelete = async (id) => {
    if (!confirm("Delete this comparison? The original photos stay in your library.")) return;
    try {
      await api.delete(`/comparisons/${id}`);
      toast.success("Comparison deleted");
      if (active?.id === id) setActive(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="photo-compare-loading">
        <div className="skeleton h-24 w-full" />
        <div className="skeleton h-32 w-full" />
      </div>
    );
  }

  return (
    <div data-testid="photo-compare-tab">
      {/* Header */}
      <div className="card mb-6 border-l-4 border-l-[#1B6C62]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-1">
              <ImgIcon size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
              <h3 className="font-serif text-xl text-[#0D2B52]">Photo Compare</h3>
            </div>
            <p className="text-[13px] text-[#5a6b7a] leading-relaxed max-w-2xl">
              When the agent sends you a photo to back up their claim, like
              &ldquo;the fans were spotless on exit&rdquo;, drop your dated photo
              next to theirs. Compass writes a plain-English comparison you can
              drop straight into a reply letter or email.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-teal inline-flex items-center gap-1.5 text-sm shrink-0"
            data-testid="photo-compare-new-btn"
          >
            <Plus size={14} strokeWidth={1.75} /> New comparison
          </button>
        </div>
      </div>

      {showForm && (
        <NewComparisonForm
          caseId={caseId}
          onClose={() => setShowForm(false)}
          onCreated={async (created) => {
            setShowForm(false);
            await reload();
            setActive(created);
          }}
        />
      )}

      {active && (
        <ComparisonDetail
          comparison={active}
          caseState={caseState}
          onClose={() => setActive(null)}
          onDelete={() => onDelete(active.id)}
          onUpdate={(updated) => setActive(updated)}
        />
      )}

      {comparisons.length === 0 && !showForm ? (
        <EmptyState
          icon={ImgIcon}
          title="No comparisons yet"
          description="When you next get a photo from the agent that doesn't match reality, this is where you stand them up next to your dated proof."
          actionLabel="Create your first comparison"
          onAction={() => setShowForm(true)}
          testid="photo-compare-empty"
        />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {comparisons.map(c => (
            <button
              key={c.id}
              onClick={() => setActive(c)}
              className="card card-hover text-left"
              data-testid={`comparison-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="font-serif text-lg text-[#0D2B52] leading-tight">{c.title}</h4>
                {c.ai_summary && <Sparkles size={13} strokeWidth={1.5} className="text-[#1B6C62] mt-1 shrink-0" />}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="aspect-[4/3] bg-[#EFEDE5] border border-[#E5E4DE] overflow-hidden">
                  <img src={c.user_side.file_url ? `${API}${c.user_side.file_url}&token=${encodeURIComponent(token() || "")}` : inlineUrl(c.user_side.document_id)}
                       alt="yours" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="aspect-[4/3] bg-[#EFEDE5] border border-[#E5E4DE] overflow-hidden">
                  <img src={c.agent_side.file_url ? `${API}${c.agent_side.file_url}&token=${encodeURIComponent(token() || "")}` : inlineUrl(c.agent_side.document_id)}
                       alt="theirs" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-[#5a6b7a] font-mono">
                <span>{new Date(c.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                <span className="text-[#1B6C62]">View →</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- New Comparison form ----------
function NewComparisonForm({ caseId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [userContext, setUserContext] = useState("");
  const [includeAi, setIncludeAi] = useState(true);
  const [busy, setBusy] = useState(false);

  // user (tenant) side
  const [uCaption, setUCaption] = useState("");
  const [uDate, setUDate] = useState("");
  const [uDocId, setUDocId] = useState(null);
  const [uFile, setUFile] = useState(null);

  // agent side
  const [aCaption, setACaption] = useState("");
  const [aDate, setADate] = useState("");
  const [aDocId, setADocId] = useState(null);
  const [aFile, setAFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give it a short title.");
    if (!userContext.trim()) return toast.error("Tell Compass what this comparison is about.");
    if (!uDocId && !uFile) return toast.error("Pick or upload your photo.");
    if (!aDocId && !aFile) return toast.error("Pick or upload the agent's photo.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("user_context", userContext);
      fd.append("user_caption", uCaption);
      if (uDate) fd.append("user_photo_date", uDate);
      if (uDocId) fd.append("user_doc_id", uDocId);
      if (uFile) fd.append("user_file", uFile);
      fd.append("agent_caption", aCaption);
      if (aDate) fd.append("agent_photo_date", aDate);
      if (aDocId) fd.append("agent_doc_id", aDocId);
      if (aFile) fd.append("agent_file", aFile);
      fd.append("include_ai_summary", String(includeAi));
      const { data } = await api.post(`/cases/${caseId}/comparisons`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(includeAi ? "Comparison saved with AI summary." : "Comparison saved.");
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save comparison.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card mb-6 border-l-4 border-l-[#0D2B52]" data-testid="photo-compare-form">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-serif text-lg text-[#0D2B52]">New photo comparison</h4>
        <button type="button" onClick={onClose} className="btn-ghost p-1.5" data-testid="photo-compare-form-close"><X size={16} strokeWidth={1.5} /></button>
      </div>

      <label className="label-mono text-[10px] block mb-1">Short title</label>
      <input
        value={title} onChange={e => setTitle(e.target.value)}
        placeholder="e.g. Ceiling fans, exit cleanliness"
        className="w-full border border-[#E5E4DE] bg-white px-3 py-2 text-sm font-serif mb-4"
        data-testid="photo-compare-title"
      />

      <label className="label-mono text-[10px] block mb-1">What&apos;s this comparison about? <span className="text-[#B35D4C]">*</span></label>
      <textarea
        value={userContext} onChange={e => setUserContext(e.target.value)}
        placeholder="e.g. The agent is trying to deduct $250 from my bond for &lsquo;dirty fans&rsquo;. They sent the photo on the right. My Photo Trail check-in from 14 Nov, on the left, shows the same fans visibly clean."
        rows={3}
        maxLength={2000}
        className="w-full border border-[#E5E4DE] bg-white px-3 py-2 text-sm font-serif mb-4"
        data-testid="photo-compare-context"
      />

      <div className="grid md:grid-cols-2 gap-5 mb-4">
        <PhotoSidePicker
          caseId={caseId}
          sideLabel="Your photo (your proof)"
          accent="#1B6C62"
          caption={uCaption} setCaption={setUCaption}
          date={uDate} setDate={setUDate}
          docId={uDocId} setDocId={setUDocId}
          file={uFile} setFile={setUFile}
          testidPrefix="photo-compare-user"
        />
        <PhotoSidePicker
          caseId={caseId}
          sideLabel="Agent's photo (their claim)"
          accent="#B35D4C"
          caption={aCaption} setCaption={setACaption}
          date={aDate} setDate={setADate}
          docId={aDocId} setDocId={setADocId}
          file={aFile} setFile={setAFile}
          testidPrefix="photo-compare-agent"
        />
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer bg-[#E6F5F2] border border-[#1B6C62]/30 p-3 mb-4" data-testid="photo-compare-ai-toggle-wrapper">
        <input
          type="checkbox" checked={includeAi}
          onChange={e => setIncludeAi(e.target.checked)}
          className="mt-0.5 accent-[#1B6C62]"
          data-testid="photo-compare-ai-toggle"
        />
        <span className="text-sm text-[#0D2B52] font-serif leading-snug">
          <strong>Have Compass write the comparison in plain English</strong>
          <span className="block text-xs text-[#6B7280] mt-0.5 italic">
            Uses 1 AI query. Recommended, it gives you a ready-made paragraph you can drop into a reply letter.
          </span>
        </span>
      </label>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="btn-teal text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="photo-compare-submit"
        >
          {busy ? <><Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> Saving…</> : <><Sparkles size={13} strokeWidth={1.5} /> Save comparison</>}
        </button>
        <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
      </div>
    </form>
  );
}

// ---------- Per-side picker: pick existing OR upload fresh ----------
function PhotoSidePicker({ caseId, sideLabel, accent, caption, setCaption, date, setDate, docId, setDocId, file, setFile, testidPrefix }) {
  const [mode, setMode] = useState("upload"); // "upload" | "existing"
  const [docs, setDocs] = useState([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = async () => {
    if (docsLoaded) return;
    try {
      const r = await api.get(`/cases/${caseId}/documents`);
      const imgs = (r.data || []).filter(d => (d.mime_type || "").startsWith("image/"));
      setDocs(imgs);
      setDocsLoaded(true);
    } catch (e) {
      toast.error("Couldn't load your photos");
    }
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === "upload") setDocId(null);
    else { setFile(null); loadDocs(); }
  };

  return (
    <div className="border-2 border-dashed p-3" style={{ borderColor: accent + "55" }} data-testid={`${testidPrefix}-side`}>
      <div className="text-xs font-mono uppercase tracking-wide mb-2" style={{ color: accent }}>{sideLabel}</div>

      <div className="flex gap-1 mb-3 text-xs">
        <button type="button" onClick={() => switchMode("upload")}
          className={`px-2.5 py-1 border ${mode === "upload" ? "bg-[#0D2B52] text-white border-[#0D2B52]" : "bg-white text-[#0D2B52] border-[#E5E4DE]"}`}
          data-testid={`${testidPrefix}-mode-upload`}
        ><Upload size={11} strokeWidth={1.75} className="inline mr-1" />Upload</button>
        <button type="button" onClick={() => switchMode("existing")}
          className={`px-2.5 py-1 border ${mode === "existing" ? "bg-[#0D2B52] text-white border-[#0D2B52]" : "bg-white text-[#0D2B52] border-[#E5E4DE]"}`}
          data-testid={`${testidPrefix}-mode-existing`}
        ><ImgIcon size={11} strokeWidth={1.75} className="inline mr-1" />Pick existing</button>
      </div>

      {mode === "upload" ? (
        <div onClick={() => fileRef.current?.click()}
          className="border border-dashed border-[#E5E4DE] hover:border-[#1B6C62] p-3 text-center cursor-pointer mb-2"
          data-testid={`${testidPrefix}-dropzone`}
        >
          {file ? (
            <div className="text-xs font-mono text-[#0D2B52] truncate">{file.name}</div>
          ) : (
            <>
              <Camera size={20} strokeWidth={1.25} className="text-[#5a6b7a] mx-auto mb-1" />
              <div className="text-xs text-[#1A1A1A]">Tap to choose a photo</div>
            </>
          )}
          <input
            ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={e => setFile(e.target.files?.[0] || null)}
            data-testid={`${testidPrefix}-file-input`}
          />
        </div>
      ) : (
        <div className="mb-2">
          {!docsLoaded ? (
            <div className="text-xs text-[#5a6b7a]"><Loader2 size={12} className="inline animate-spin" /> Loading your photos…</div>
          ) : docs.length === 0 ? (
            <div className="text-xs text-[#5a6b7a] italic">No image files yet in this case. Upload one instead.</div>
          ) : (
            <div className="max-h-44 overflow-y-auto grid grid-cols-3 gap-1.5">
              {docs.map(d => (
                <button
                  key={d.id} type="button"
                  onClick={() => { setDocId(d.id); if (!caption && d.title) setCaption(d.title); if (!date && d.event_date) setDate(d.event_date); }}
                  className={`relative aspect-[4/3] border-2 overflow-hidden ${docId === d.id ? "border-[#1B6C62]" : "border-transparent"}`}
                  data-testid={`${testidPrefix}-pick-${d.id}`}
                  title={`${d.title || d.original_name}${d.event_date ? ` · ${d.event_date}` : ""}`}
                >
                  <img src={inlineUrl(d.id)} alt={d.original_name} className="w-full h-full object-cover" loading="lazy" />
                  {docId === d.id && (
                    <div className="absolute inset-0 bg-[#1B6C62]/30 flex items-center justify-center">
                      <Check size={14} className="text-white" strokeWidth={2.5} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <input
        value={caption} onChange={e => setCaption(e.target.value)}
        placeholder="Caption (e.g. Master bedroom fan)"
        className="w-full border border-[#E5E4DE] bg-white px-2 py-1.5 text-xs font-serif mb-1.5"
        data-testid={`${testidPrefix}-caption`}
      />
      <input
        value={date} onChange={e => setDate(e.target.value)}
        type="date"
        className="w-full border border-[#E5E4DE] bg-white px-2 py-1.5 text-xs font-mono"
        data-testid={`${testidPrefix}-date`}
      />
    </div>
  );
}

// ---------- Detail view: composite preview + AI summary + drop-into-letter ----------
function ComparisonDetail({ comparison, caseState, onClose, onDelete, onUpdate }) {
  const [templates, setTemplates] = useState([]);
  const [tmpl, setTmpl] = useState("");
  const [mode, setMode] = useState("attachment");
  const [letterText, setLetterText] = useState("");
  const [letterMeta, setLetterMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/letter-templates").then(({ data }) => {
      setTemplates(data);
      if (data?.length && !tmpl) setTmpl(data[0].key);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drop = async () => {
    if (!tmpl) return toast.error("Pick a letter template first.");
    setBusy(true); setLetterText(""); setLetterMeta(null);
    try {
      const fd = new FormData();
      fd.append("template_key", tmpl);
      fd.append("mode", mode);
      const { data } = await api.post(`/comparisons/${comparison.id}/drop-into-letter`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLetterText(data.text);
      setLetterMeta(data);
      // If a composite Document was just materialised, push the update up
      if (data.composite_doc_id && !comparison.composite_doc_id) {
        onUpdate?.({ ...comparison, composite_doc_id: data.composite_doc_id });
      }
      toast.success("Letter drafted. Review carefully before sending.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't draft the letter.");
    } finally {
      setBusy(false);
    }
  };

  const copyLetter = () => {
    navigator.clipboard.writeText(letterText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="card mb-6 border-l-4 border-l-[#0D2B52]" data-testid="comparison-detail">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-serif text-xl text-[#0D2B52] mb-0.5">{comparison.title}</h3>
          <div className="text-[11px] text-[#5a6b7a] font-mono">
            <Calendar size={10} className="inline -mt-0.5 mr-1" />
            {new Date(comparison.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onDelete} className="btn-ghost p-1.5 text-[#B35D4C]" title="Delete" data-testid="comparison-delete-btn">
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
          <button onClick={onClose} className="btn-ghost p-1.5" title="Close" data-testid="comparison-close-btn">
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <p className="text-[13px] text-[#1A1A1A] font-serif italic mb-4 border-l-2 border-[#E5E4DE] pl-3">
        {comparison.user_context}
      </p>

      {/* Composite preview */}
      <div className="mb-4">
        <div className="label-mono text-[10px] mb-1.5">Side-by-side composite</div>
        <a href={compositeUrl(comparison.id)} target="_blank" rel="noopener noreferrer" className="block border border-[#E5E4DE] bg-[#EFEDE5] hover:opacity-90">
          <img src={compositeUrl(comparison.id)} alt="comparison composite" className="w-full h-auto" data-testid="comparison-composite-img" />
        </a>
      </div>

      {/* AI summary */}
      {comparison.ai_summary ? (
        <div className="mb-5 bg-[#FAF6E8] border border-[#C9A961]/40 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={13} strokeWidth={1.5} className="text-[#8A6D3B]" />
            <span className="label-mono text-[10px] text-[#8A6D3B]">Compass&apos;s plain-English read</span>
          </div>
          <div className="text-sm font-serif text-[#1A1A1A] whitespace-pre-wrap leading-relaxed" data-testid="comparison-summary">
            {comparison.ai_summary}
          </div>
        </div>
      ) : (
        <div className="mb-5 text-xs text-[#5a6b7a] italic">No AI summary saved for this comparison. Compass will generate one on demand when you drop it into a letter.</div>
      )}

      {/* Drop-into-letter */}
      <div className="border-t border-[#E5E4DE] pt-4">
        <ShareSection comparison={comparison} onUpdate={onUpdate} />
      </div>

      {/* Drop-into-letter */}
      <div className="border-t border-[#E5E4DE] pt-4 mt-4">
        <h4 className="font-serif text-[16px] text-[#0D2B52] mb-1">Drop this into a letter</h4>
        <p className="text-[12px] text-[#5a6b7a] mb-3">
          Compass will draft a letter that uses this comparison. Pick a template and how you&apos;d like the comparison included.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-mono text-[10px] block mb-1">Letter template</label>
            <div className="relative">
              <select
                value={tmpl} onChange={e => setTmpl(e.target.value)}
                className="w-full border border-[#E5E4DE] bg-white px-3 py-2 text-sm font-serif appearance-none pr-8"
                data-testid="comparison-template-select"
              >
                {templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <ChevronDown size={14} strokeWidth={1.5} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="label-mono text-[10px] block mb-1">How to include the comparison</label>
            <div className="flex gap-1.5 text-xs">
              <button type="button"
                onClick={() => setMode("attachment")}
                className={`flex-1 px-2.5 py-2 border ${mode === "attachment" ? "bg-[#0D2B52] text-white border-[#0D2B52]" : "bg-white text-[#0D2B52] border-[#E5E4DE]"}`}
                data-testid="comparison-mode-attachment"
                title="Letter refers to the composite as an attached image"
              >
                PDF attachment
              </button>
              <button type="button"
                onClick={() => setMode("paragraph")}
                className={`flex-1 px-2.5 py-2 border ${mode === "paragraph" ? "bg-[#0D2B52] text-white border-[#0D2B52]" : "bg-white text-[#0D2B52] border-[#E5E4DE]"}`}
                data-testid="comparison-mode-paragraph"
                title="Insert the AI summary as a paragraph in the letter body"
              >
                Inline paragraph
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={drop} disabled={busy || !tmpl}
          className="btn-teal text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="comparison-drop-btn"
        >
          {busy ? (<><Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> Drafting…</>) : (<><ArrowRight size={13} strokeWidth={1.5} /> Draft letter</>)}
        </button>

        {letterText && (
          <div className="mt-5 border-t border-[#E5E4DE] pt-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="label-mono text-[10px]">Your draft letter</span>
              <div className="flex items-center gap-2">
                {letterMeta?.composite_doc_id && mode === "attachment" && (
                  <a
                    href={`${API}/documents/${letterMeta.composite_doc_id}/download?token=${encodeURIComponent(token() || "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid="comparison-download-composite"
                  >
                    <FileText size={12} strokeWidth={1.5} /> Download attachment
                  </a>
                )}
                <button onClick={copyLetter} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="comparison-copy-letter">
                  {copied ? (<><Check size={11} /> Copied</>) : (<><Copy size={11} strokeWidth={1.5} /> Copy</>)}
                </button>
              </div>
            </div>
            <div className="whitespace-pre-wrap font-serif text-[14px] leading-relaxed text-[#1A1A1A] bg-white border border-[#E5E4DE] p-5">
              <RichText text={letterText} annexMap={{}} state={caseState} />
            </div>
            <p className="text-xs text-[#6B7280] mt-2 italic">Review carefully before sending. This is drafting help, not legal advice.</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Share section (turn the comparison into a public anonymous page) ----------
function ShareSection({ comparison, onUpdate }) {
  const [showPhotos, setShowPhotos] = useState(comparison.share_show_photos ?? true);
  const [showSummary, setShowSummary] = useState(comparison.share_show_summary ?? true);
  const [showContext, setShowContext] = useState(comparison.share_show_context ?? false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const enabled = !!comparison.share_enabled;
  const shareToken = comparison.share_token;
  const sharePath = shareToken ? `/c/${shareToken}` : null;
  const fullUrl = sharePath ? `${window.location.origin}${sharePath}` : "";

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("show_photos", String(showPhotos));
      fd.append("show_summary", String(showSummary));
      fd.append("show_context", String(showContext));
      const { data } = await api.post(`/comparisons/${comparison.id}/share`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate?.({
        ...comparison,
        share_enabled: true,
        share_token: data.share_token,
        share_show_photos: data.share_show_photos,
        share_show_summary: data.share_show_summary,
        share_show_context: data.share_show_context,
      });
      toast.success(enabled ? "Share settings updated." : "Comparison is now public.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't enable sharing.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!confirm("Take this comparison offline? The public page will stop working immediately. (The link is kept, you can re-enable any time.)")) return;
    setBusy(true);
    try {
      await api.delete(`/comparisons/${comparison.id}/share`);
      onUpdate?.({ ...comparison, share_enabled: false });
      toast.success("Sharing turned off.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't disable.");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const shareText = encodeURIComponent(`${comparison.title}, verified photo comparison via TenantTrail`);
  const shareUrl = encodeURIComponent(fullUrl);

  return (
    <div data-testid="comparison-share-section">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h4 className="font-serif text-[16px] text-[#0D2B52] mb-0.5 flex items-center gap-1.5">
            <Share2 size={14} strokeWidth={1.5} className="text-[#1B6C62]" />
            Share publicly
          </h4>
          <p className="text-[12px] text-[#5a6b7a]">
            Generate an <strong>anonymous</strong> public page you can post to a tenancy Facebook group, send to an agent, or include in a tribunal email. <strong>No tenant name or address is ever shown.</strong>
          </p>
        </div>
        {enabled && (
          <span className="text-[10px] font-mono px-2 py-1 bg-[#1B6C62] text-white shrink-0" data-testid="share-status-live">
            LIVE
          </span>
        )}
      </div>

      <div className="bg-[#F4F3ED] border border-[#E5E4DE] p-3 sm:p-4">
        <div className="label-mono text-[10px] mb-2">What to include on the public page</div>
        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          <label className="flex items-start gap-2 cursor-pointer p-2 bg-white border border-[#E5E4DE] hover:border-[#1B6C62]">
            <input type="checkbox" checked={showPhotos} onChange={e => setShowPhotos(e.target.checked)} className="mt-0.5 accent-[#1B6C62]" data-testid="share-toggle-photos" />
            <span className="text-xs text-[#0D2B52]">
              <strong className="block">Photos</strong>
              <span className="text-[#6B7280]">The side-by-side composite with watermark</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 bg-white border border-[#E5E4DE] hover:border-[#1B6C62]">
            <input type="checkbox" checked={showSummary} onChange={e => setShowSummary(e.target.checked)} className="mt-0.5 accent-[#1B6C62]" data-testid="share-toggle-summary" />
            <span className="text-xs text-[#0D2B52]">
              <strong className="block">AI summary</strong>
              <span className="text-[#6B7280]">Compass&apos;s plain-English read</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 bg-white border border-[#E5E4DE] hover:border-[#1B6C62]">
            <input type="checkbox" checked={showContext} onChange={e => setShowContext(e.target.checked)} className="mt-0.5 accent-[#1B6C62]" data-testid="share-toggle-context" />
            <span className="text-xs text-[#0D2B52]">
              <strong className="block">Your context paragraph</strong>
              <span className="text-[#6B7280]">Your own framing, review it first for PII</span>
            </span>
          </label>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={save} disabled={busy} className="btn-teal text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="share-save-btn">
            {busy ? <><Loader2 size={12} strokeWidth={1.5} className="animate-spin" /> Saving…</> : (enabled ? <><Check size={12} strokeWidth={1.5} /> Update share</> : <><Globe size={12} strokeWidth={1.5} /> Publish anonymously</>)}
          </button>
          {enabled && (
            <button onClick={disable} disabled={busy} className="btn-ghost text-sm inline-flex items-center gap-1.5 text-[#B35D4C]" data-testid="share-disable-btn">
              <EyeOff size={12} strokeWidth={1.5} /> Take offline
            </button>
          )}
        </div>

        {enabled && shareToken && (
          <div className="border-t border-[#E5E4DE] pt-3 mt-2" data-testid="share-live-block">
            <div className="label-mono text-[10px] mb-1.5">Public link</div>
            <div className="flex gap-1.5 mb-3">
              <input
                readOnly
                value={fullUrl}
                onClick={(e) => e.target.select()}
                className="flex-1 bg-white border border-[#E5E4DE] px-2.5 py-1.5 text-xs font-mono text-[#0D2B52]"
                data-testid="share-public-url"
              />
              <button onClick={copyUrl} className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0" data-testid="share-copy-btn">
                {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} strokeWidth={1.5} /> Copy</>}
              </button>
              <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0" data-testid="share-preview-btn">
                <Eye size={11} strokeWidth={1.5} /> Preview
              </a>
            </div>

            <div className="label-mono text-[10px] mb-1.5">Share to</div>
            <div className="flex flex-wrap gap-1.5">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#1877F2] text-white text-xs hover:opacity-90" data-testid="share-fb-btn">
                <Facebook size={12} strokeWidth={1.75} /> Facebook
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-black text-white text-xs hover:opacity-90" data-testid="share-x-btn">
                <Twitter size={12} strokeWidth={1.75} /> X
              </a>
              <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#25D366] text-white text-xs hover:opacity-90" data-testid="share-whatsapp-btn">
                <MessageCircle size={12} strokeWidth={1.75} /> WhatsApp
              </a>
            </div>

            <p className="text-[11px] text-[#6B7280] italic mt-3">
              Every social preview will show the watermarked composite, Facebook readers see a TenantTrail card before they even click through.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
